import React, { useState, useEffect } from 'react'; // Added useEffect
import { useUser } from '@clerk/clerk-react';
import {
    MessageCircle, Brain, Layers, Database, Mail, ArrowRight, AlertTriangle,
    CheckCircle2, LineChart, Rocket, Info, Clock, Trophy, X, // Added X icon
} from 'lucide-react';
import { animated, useSpring, easings } from 'react-spring';
import Navbar from '../../components/navbar/Navbar.jsx'; // Ensure this path is correct
import GamefiedAIAssistant from '../redstone/intercom.jsx'; // Ensure this path is correct - KEPT

// Status configuration - unchanged
const statusConfig = {
    operational: { bgColor: 'bg-green-100', textColor: 'text-green-700', icon: CheckCircle2, borderColor: 'border-green-200' },
    degraded: { bgColor: 'bg-yellow-100', textColor: 'text-yellow-800', icon: AlertTriangle, borderColor: 'border-yellow-200' },
    down: { bgColor: 'bg-red-100', textColor: 'text-red-700', icon: AlertTriangle, borderColor: 'border-red-200' },
    maintenance: { bgColor: 'bg-blue-100', textColor: 'text-blue-700', icon: Clock, borderColor: 'border-blue-200' }
};

// --- Product Card Component - (No changes needed here) ---
const ProductCard = ({ product }) => {
    const isFoundry = product.id === 'echidna-ai-foundry';
    const isRedstone = product.id === 'redstone-ai';

    // --- Animations remain the same ---
    const cardAnimation = useSpring({
        loop: true,
        ...(isFoundry && product.status === 'operational'
            ? { /* Foundry animation */
                from: { scale: 1, boxShadow: '0 0 0px rgba(59, 130, 246, 0)' },
                to: [
                    { scale: 1.02, boxShadow: '0 0 8px rgba(59, 130, 246, 0.3)' }, // Slightly smaller shadow
                    { scale: 1, boxShadow: '0 0 0px rgba(59, 130, 246, 0)' }
                ],
                config: { duration: 2000, easing: easings.easeInOutSine },
            }
            : { /* Default state */
                scale: 1,
                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                config: { duration: 0 }
            })
    });
    const statusAnimation = useSpring({
        loop: true,
        ...(product.status === 'operational'
            ? { opacity: [0.8, 1, 0.8], transform: ['scale(1)', 'scale(1.03)', 'scale(1)'], config: { duration: 1500 } } // Slightly smaller scale
            : product.status === 'maintenance'
            ? { transform: ['rotate(0deg)', 'rotate(360deg)'], config: { duration: 3000 } }
            : product.status === 'degraded'
            ? { transform: ['translateX(0px)', 'translateX(1.5px)', 'translateX(-1.5px)', 'translateX(0px)'], config: { duration: 500 } } // Slightly less shake
            : product.status === 'down'
            ? { color: ['#f44336', '#d32f2f', '#f44336'], config: { duration: 750 } }
            : {})
    });

    const StatusIcon = statusConfig[product.status].icon;

    // Navigate on click
    const handleClick = () => {
        if (product.link) window.location.href = product.link;
        else console.warn(`Product ${product.name} has no link.`);
    };

    // Handle key press for accessibility
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
        }
    };

    // Condensed status text logic
    const statusText = {
        operational: 'Operational',
        degraded: 'Degraded',
        maintenance: 'Maintenance',
        down: 'Down'
    }[product.status] || 'Unknown';

    return (
        <animated.div
            style={cardAnimation}
            className={`bg-white border border-gray-300 rounded-md overflow-hidden transition-shadow hover:shadow-md focus:shadow-md focus:outline-none focus:ring-1 focus:ring-offset-1 ${isRedstone ? 'focus:ring-red-400' : 'focus:ring-blue-400'} cursor-pointer flex flex-col h-full`} // Slightly reduced shadow/ring
            onClick={handleClick}
            onKeyPress={handleKeyPress}
            role="button"
            tabIndex="0"
            aria-label={`Access ${product.name}`}
        >
            {/* Header */}
            <div className="bg-gray-50 px-3 py-2 border-b border-gray-300"> {/* Reduced padding */}
                <div className="flex items-center gap-2"> {/* Reduced gap */}
                    <div className="p-1 bg-white rounded border border-gray-300"> {/* Reduced padding */}
                        {React.createElement(product.icon, { className: "w-3.5 h-3.5 text-gray-600" })} {/* Slightly smaller icon */}
                    </div>
                    <div>
                        <h3 className="text-xs text-gray-800 font-medium">{product.name}</h3> {/* Smaller text */}
                        <p className="text-[11px] text-gray-500">{product.description}</p> {/* Smaller text */}
                    </div>
                </div>
            </div>

            {/* Body */}
            <div className="p-3 flex-grow flex flex-col justify-between"> {/* Reduced padding */}
                <div>
                    <animated.div style={statusAnimation} className={`inline-flex items-center gap-1 p-1.5 rounded border ${statusConfig[product.status].borderColor} mb-2`}> {/* Reduced padding/gap/margin */}
                        {React.createElement(StatusIcon, { className: `w-3 h-3 ${statusConfig[product.status].textColor}` })} {/* Smaller icon */}
                        <span className={`text-xs font-medium ${statusConfig[product.status].textColor}`}>
                            {statusText}
                        </span>
                    </animated.div>

                    <div className="grid grid-cols-2 gap-3"> {/* Reduced gap */}
                        <div className="text-xs">
                            <span className="block text-gray-500 mb-0.5">Uptime</span> {/* Reduced margin */}
                            <span className="font-medium text-gray-700">{product.metrics.uptime}</span> {/* Slightly lighter text */}
                        </div>
                        <div className="text-xs">
                            <span className="block text-gray-500 mb-0.5">Response</span> {/* Shortened label, reduced margin */}
                            <span className="font-medium text-gray-700">{product.metrics.response}</span> {/* Slightly lighter text */}
                        </div>
                    </div>
                </div>

                {/* Footer Hint */}
                <div className="mt-3 pt-2 border-t border-gray-200 flex justify-end"> {/* Reduced margin/padding */}
                    <span className="text-[11px] text-gray-500 italic"> {/* Smaller text */}
                        Click to access
                    </span>
                </div>
            </div>
        </animated.div>
    );
};


// --- Main Product Dashboard Component - With Status Alert Popup ---
const ProductDashboard = () => {
    const { user } = useUser();
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [showStatusAlert, setShowStatusAlert] = useState(false); // State for popup visibility
    const [affectedServices, setAffectedServices] = useState([]); // State for affected services list
    const [alertType, setAlertType] = useState('maintenance'); // 'maintenance' or 'down' for styling

    // Products data - (ensure status reflects reality for testing)
    const products = [
        { id: 'echidna-chat', name: 'Echidna Chat', icon: MessageCircle, description: 'Enterprise Communication', status: 'down', category: 'communication', metrics: { uptime: '0%', response: '-' }, link: '' },
        { id: 'echidna-llm', name: 'Echidna LLM', icon: Brain, description: 'Language Processing', status: 'maintenance', category: 'ai', metrics: { uptime: '0%', response: '-' }, link: '' },
        { id: 'neptaverse', name: 'Neptaverse XR', icon: Layers, description: 'Extended Reality Engine', status: 'maintenance', category: 'xr', metrics: { uptime: '0%', response: '-' }, link: '' },
        { id: 'data-pipeline', name: 'Data Pipeline', icon: Database, description: 'Analytics Infrastructure', status: 'operational', category: 'data', metrics: { uptime: '99.2%', response: '110ms' }, link: '/echidnamail/dap' },
        { id: 'redstone-ai', name: 'Redstone AI', icon: Rocket, description: 'Customer Dev Engine', status: 'operational', category: 'ai', metrics: { uptime: '99.95%', response: '70ms' }, link: '/redstone/dash' },
        { id: 'echidna-ai-foundry', name: 'AI Foundry', icon: Trophy, description: 'AI Model Platform', status: 'operational', category: 'ai', metrics: { uptime: '99.8%', response: '95ms' }, link: '/echidna/efoundry/dashboard' },
        { id: 'arc-analytics', name: 'Arc Analytics', icon: LineChart, description: 'Website Analysis', status: 'operational', category: 'data', metrics: { uptime: '99.96%', response: '85ms' }, link: '/echidna/arc' },
        { id: 'echidna-mail', name: 'Echidna Mail', icon: Mail, description: 'Secure Email', status: 'operational', category: 'communication', metrics: { uptime: '100%', response: '30ms' }, link: '/echidnamail/dashboard' }
    ];

    // Categories - unchanged structure
    const categories = [
        { id: 'all', name: 'All' },
        { id: 'communication', name: 'Communication' },
        { id: 'ai', name: 'AI' },
        { id: 'data', name: 'Data' },
        { id: 'xr', name: 'XR' }
    ];

    // --- NEW: useEffect to check status on mount ---
    useEffect(() => {
        const nonOperationalServices = products.filter(
            p => p.status === 'down' || p.status === 'maintenance'
        );

        if (nonOperationalServices.length > 0) {
            setAffectedServices(nonOperationalServices); // Store the affected services
            // Determine alert type (prioritize 'down' for red styling)
            const isDown = nonOperationalServices.some(p => p.status === 'down');
            setAlertType(isDown ? 'down' : 'maintenance');
            setShowStatusAlert(true); // Show the popup
        }
    }, []); // Empty dependency array ensures this runs only once on mount

    const filteredProducts = selectedCategory === 'all'
        ? products
        : products.filter(product => product.category === selectedCategory);

    const getStatusSummary = () => {
        const counts = products.reduce((acc, p) => { acc[p.status] = (acc[p.status] || 0) + 1; return acc; }, {});
        if (counts.down > 0) return { type: 'down', text: 'Service Outage' };
        if (counts.maintenance > 0) return { type: 'maintenance', text: 'Maintenance' };
        if (counts.degraded > 0) return { type: 'degraded', text: 'Degraded' };
        return { type: 'operational', text: 'All Operational' };
    };

    const statusSummary = getStatusSummary();

    const handleCategoryKeyPress = (e, categoryId) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setSelectedCategory(categoryId);
        }
    };

    // --- Get config for the alert popup ---
    const alertPopupConfig = statusConfig[alertType]; // Use 'down' or 'maintenance' config

    return (
        <div className="min-h-screen bg-gray-100 relative"> {/* Added relative positioning */}
            <Navbar />

            {/* --- NEW: Status Alert Popup --- */}
            {showStatusAlert && (
                <div className={`fixed top-5 right-5 z-50 max-w-sm w-full p-4 rounded-md shadow-lg border ${alertPopupConfig.borderColor} ${alertPopupConfig.bgColor}`}>
                    <div className="flex items-start gap-3">
                        <div className={`flex-shrink-0 ${alertPopupConfig.textColor}`}>
                            {React.createElement(alertPopupConfig.icon, { className: "w-5 h-5", "aria-hidden": "true" })}
                        </div>
                        <div className="flex-1">
                            <p className={`text-sm font-medium ${alertPopupConfig.textColor}`}>
                                {alertType === 'down' ? 'Service Outage Alert' : 'Service Maintenance Notice'}
                            </p>
                            <p className={`mt-1 text-sm ${alertPopupConfig.textColor}`}>
                                The following services are currently affected:
                            </p>
                            <ul className="list-disc list-inside mt-1 text-sm">
                                {affectedServices.map(service => (
                                    <li key={service.id} className={`${alertPopupConfig.textColor}`}>
                                        {service.name} ({service.status})
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="ml-auto pl-3">
                            <div className="-mx-1.5 -my-1.5">
                            <div
    onClick={() => setShowStatusAlert(false)}
    className={`inline-flex rounded-md p-1.5 cursor-pointer ${alertPopupConfig.textColor} hover:bg-opacity-80 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 ${alertType === 'down' ? 'focus:ring-offset-red-100 focus:ring-red-600' : 'focus:ring-offset-blue-100 focus:ring-blue-600'}`}
    role="button"
    tabIndex={0}
    aria-label="Dismiss notification"
    onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            setShowStatusAlert(false);
        }
    }}
>
    <span className="sr-only">Dismiss</span>
    <X className="h-4 w-4" aria-hidden="true" />
</div>

                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* --- End Status Alert Popup --- */}


            <div className="px-4 sm:px-6 lg:px-8 py-5 max-w-7xl mx-auto">
                {/* Header Section */}
                <div className="mb-5">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                        <div>
                            <h1 className="text-lg font-semibold text-gray-800">Service Health</h1>
                            <p className="text-gray-600 mt-0.5 text-sm">Monitor service status and access products.</p>
                        </div>
                        <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded ${statusConfig[statusSummary.type].bgColor}`}>
                            {React.createElement(statusConfig[statusSummary.type].icon, {
                                className: `w-3.5 h-3.5 ${statusConfig[statusSummary.type].textColor}`
                            })}
                            <span className={`text-xs font-medium ${statusConfig[statusSummary.type].textColor}`}>{statusSummary.text}</span>
                        </div>
                    </div>
                    {/* Category Filters */}
                    <div className="bg-white border border-gray-300 rounded-md shadow-sm overflow-hidden">
                         <div className="bg-gray-50 border-b border-gray-300 px-3 py-1.5">
                             <div className="flex gap-1.5 overflow-x-auto hide-scrollbar">
                                 {categories.map(category => (
                                     <div
                                         key={category.id}
                                         role="button"
                                         tabIndex="0"
                                         onClick={() => setSelectedCategory(category.id)}
                                         onKeyPress={(e) => handleCategoryKeyPress(e, category.id)}
                                         className={`px-2.5 py-1 rounded text-xs whitespace-nowrap transition-all duration-150 ease-in-out cursor-pointer focus:outline-none focus:ring-1 focus:ring-offset-1 ${
                                             selectedCategory === category.id
                                                 ? 'bg-white text-gray-800 border border-gray-300 shadow-sm ring-blue-500 font-medium'
                                                 : 'text-gray-600 hover:bg-gray-200 ring-transparent'
                                         }`}
                                         aria-pressed={selectedCategory === category.id}
                                     >
                                         {category.name}
                                     </div>
                                 ))}
                             </div>
                         </div>
                     </div>
                </div>

                {/* Product Grid */}
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {filteredProducts.map((product) => (
                        <ProductCard
                            key={product.id}
                            product={product}
                        />
                    ))}
                    {/* No products message */}
                    {filteredProducts.length === 0 && (
                        <p className="text-gray-500 text-sm sm:col-span-2 lg:col-span-3 xl:col-span-4 text-center py-6">
                            No services in "{categories.find(c => c.id === selectedCategory)?.name || selectedCategory}" category.
                        </p>
                    )}
                </div>
            </div>

            {/* Kept Assistant */}
            <div>
                <GamefiedAIAssistant />
            </div>

        </div>
    );
};

export default ProductDashboard;

// Helper CSS (place in global CSS or <style>):
/*
.hide-scrollbar::-webkit-scrollbar { display: none; }
.hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
*/